/* eslint-disable @angular-eslint/no-input-rename */
import { Component, computed, effect, inject, input, OnInit, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DxDataGridComponent, DxDataGridModule } from 'devextreme-angular';
import { DxiColumnModule } from 'devextreme-angular/ui/nested';
import { UserRoleService, UserPermissionService } from '../services';
import { UserRole, UserPermission } from '../models';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { GenericModalService } from '../../shared/generic-modal/generic-modal.service';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { SelectionChangedEvent } from 'devextreme/ui/data_grid';

@Component({
  selector: 'app-user-role-details',
  templateUrl: './user-role-details.component.html',
  styleUrls: ['./user-role-details.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [PermissionRequiredDirective, FormsModule, DxDataGridModule, BizDataGridDirective, DxiColumnModule]
})
export class UserRoleDetailsComponent implements OnInit {
  private router = inject(Router);
  private loadingPanelService = inject(LoadingPanelService);
  private toastService = inject(ToastService);
  private modalService = inject(GenericModalService);
  private userPermissionService = inject(UserPermissionService);
  private userRoleService = inject(UserRoleService);

  private grid = viewChild(DxDataGridComponent);

  paramRoleId = input(0, { alias: 'id' });
  paramCreateNew = input('', { alias: 'createNew' });

  allUserPermissions = signal<UserPermission[]>([]);
  allUserRoles = signal<UserRole[]>([]);  
  isEditing = signal(false);
  onlySelected = signal(false);
  visiblePermissions = computed(() => this.onlySelected() ? this.currentRole().Permissions ?? [] : this.allUserPermissions());
  currentRole = signal(new UserRole());
  originalRole = signal(new UserRole());
  nameAlreadyExists = computed(() => {
    const name = this.currentRole().RoleName?.trim().toLowerCase();
    if (!name) 
      return false;
    return this.allUserRoles().some(r => r.RoleId !== this.currentRole().RoleId && r.RoleName?.trim().toLowerCase() === name);
  });
  updateSelected = effect(() => {
    if (this.visiblePermissions().length > 0) {
      this.grid().selectedRowKeys = this.currentRole().Permissions ?? [];
    }
  });

  ngOnInit(): void {
    void this.initAsync();
  }

  async initAsync(): Promise<void> {
    this.loadingPanelService.showLoadingPanelDirect('Loading User Role...');
    try {
      if (this.paramCreateNew()?.localeCompare('true') === 0) {
        this.currentRole.set(new UserRole());
        this.edit();
      }
      else {
        const role = await this.userRoleService.getUserRole(this.paramRoleId());
        role.Permissions ??= [];
        role.Permissions = role.Permissions.sort((p1, p2) => p1.PermissionName.localeCompare(p2.PermissionName));
        this.currentRole.set(role);
      }

      let permissions = await this.userPermissionService.getUserPermissions();
      permissions = permissions.sort((p1, p2) => p1.PermissionName.localeCompare(p2.PermissionName));
      this.allUserPermissions.set(permissions);

      const roles = await this.userRoleService.getUserRoles();
      this.allUserRoles.set(roles);
    }
    finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }

  selectionChanged(event: SelectionChangedEvent) {
    const selectedRows = Object.assign([] as UserPermission[], event.selectedRowsData);
    const role = Object.assign(new UserRole(), this.currentRole());
    role.Permissions = selectedRows.sort((p1, p2) => p1.PermissionName.localeCompare(p2.PermissionName));
    this.currentRole.set(role);
  }

  updateRoleName(name: string) {
    const role = Object.assign(new UserRole(), this.currentRole());
    role.RoleName = name;
    this.currentRole.set(role);
  }

  edit() {
    this.originalRole.set(Object.assign(new UserRole(), this.currentRole()));
    this.isEditing.set(true);
  }

  async delete(): Promise<void> {
    try {
      await this.modalService.confirmDirect('Delete user role', 'Are you sure you want to delete this user role?');
      await this.userRoleService.deleteUserRole(this.currentRole().RoleId);
      await this.router.navigate(['user/roles']);
    } catch {
      // User cancelled or an error occurred; do nothing
    }
  }

  async save(): Promise<void> {
    this.loadingPanelService.showLoadingPanelDirect('Saving user role...');

    this.isEditing.set(false);
    this.onlySelected.set(false);

    try {
      const newRole = await this.userRoleService.saveUserRole(this.currentRole());
      this.currentRole.set(newRole);

      this.toastService.showToast('User role saved...', ToastType.success);

      if (this.paramRoleId() !== newRole.RoleId) {
        await this.router.navigate(['user/roles/details', { id: newRole.RoleId }], { replaceUrl: true });
      }
    }
    catch {
      // Ignore errors
    }
    finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }

  async cancel(): Promise<void> {
    this.isEditing.set(false);
    this.currentRole.set(this.originalRole());
    this.onlySelected.set(false);

    if (!this.currentRole().RoleId) {
      await this.router.navigate(['user/roles']);
    }
  }
}
